<script setup lang="ts">
  import SearchIcon from '../Icons/SearchIcon.vue'
  import CloseIcon from '../Icons/CloseIcon.vue'
  import SearchSuggestionItem from './SearchSuggestionItem.vue'

  const query = defineModel<string>()
  const searchInput = ref<string>()
  const isSuggestionsVisible = ref(false)
  const selectedIndex = ref(-1)
  const isSticky = ref(false)
  const searchboxRef = ref<HTMLElement | null>(null)
  const { t } = useI18n()

  onMounted(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        isSticky.value = entry.intersectionRatio < 1
      },
      { threshold: 1 }
    )

    if (searchboxRef.value instanceof HTMLElement) {
      observer.observe(searchboxRef.value)
    }
    if (!query.value && props.initialQuery) {
      query.value = props.initialQuery
    }
  })

  export type SearchboxProps = {
    display?: 'onImage' | 'solid'
    variant?: 'default' | 'v2' | 'v2-big-yolo' | 'v3'
    placeholder?: string
    autofocus?: boolean
    suggestionHeader?: string
    suggestions?: SearchSuggestions
    sticky?: boolean
    initialQuery?: string
  }

  const props = withDefaults(defineProps<SearchboxProps>(), {
    display: 'solid',
    variant: 'default',
    placeholder: '',
    autofocus: false,
    sticky: true,
    suggestionHeader: '',
    suggestions: () => [],
    initialQuery: '',
  })

  watch(
    [() => props.suggestions, () => query.value],
    ([newSuggestions, newQuery]: [SearchSuggestions, typeof query.value]) => {
      if (newQuery && newSuggestions.length === 0) {
        hideSuggestions()
      } else if (!newQuery && newSuggestions.length === 0) {
        hideSuggestions()
      } else if (newQuery) {
        showSuggestions()
      }
    },
    { immediate: true }
  )

  watch(
    () => props.initialQuery,
    (newVal) => {
      query.value = newVal ?? ''
    }
  )

  type SearchboxEmits = {
    (event: 'clickSuggestionItem', query: Suggestion): void
    (
      event:
        | 'pressEnterOnSearchInput'
        | 'updateSuggestions'
        | 'pressSearchButton',
      query: string
    ): void
  }

  const emit = defineEmits<SearchboxEmits>()

  function hideSuggestions() {
    isSuggestionsVisible.value = false
  }
  function showSuggestions() {
    isSuggestionsVisible.value = true
  }
  function resetSelectedSuggestionIndex() {
    selectedIndex.value = -1
  }
  const handleClickSuggestion = (item: Suggestion) => {
    hideSuggestions()
    cleanSearchboxInput()
    emit('clickSuggestionItem', item)
  }

  function onEnter() {
    if (!query.value) return
    emit('pressEnterOnSearchInput', query.value)

    hideSuggestions()
    if (props.initialQuery && props.initialQuery === query.value) return

    cleanSearchboxInput()
  }

  function onSearchButtonPress() {
    if (!query.value) return
    hideSuggestions()
    cleanSearchboxInput()
    emit('pressSearchButton', query.value || '')
  }

  function handleKeyDown() {
    const suggestionSize = props.suggestions.length - 1
    if (selectedIndex.value === -1) {
      selectedIndex.value = 0
      query.value = props.suggestions[selectedIndex.value].text
    } else if (suggestionSize === selectedIndex.value) {
      resetSelectedSuggestionIndex()
    } else {
      selectedIndex.value++
      query.value = props.suggestions[selectedIndex.value].text
    }
  }

  function handleKeyUp() {
    if (selectedIndex.value === -1) {
      selectedIndex.value = props.suggestions.length - 1
    } else if (selectedIndex.value > 0) {
      selectedIndex.value--
    } else {
      resetSelectedSuggestionIndex()
    }

    query.value =
      selectedIndex.value === -1
        ? searchInput.value
        : props.suggestions[selectedIndex.value].text

    setTimeout(setCaretToEnd, 0)
  }

  function handleMouseOver(index: number) {
    selectedIndex.value = index
  }

  function handleMouseLeave() {
    resetSelectedSuggestionIndex()
  }

  function setCaretToEnd() {
    const inputElement = document.getElementById('searchInput')

    if (inputElement instanceof HTMLInputElement) {
      const inputLength = inputElement.value.length
      inputElement.setSelectionRange(inputLength, inputLength)
    }
  }

  function cleanSearchboxInput() {
    query.value = ''
    searchInput.value = ''
    resetSelectedSuggestionIndex()
    emit('updateSuggestions', query.value)
  }

  function openSuggestionsDropdown() {
    if (props.suggestions.length > 0) {
      showSuggestions()
    }
  }
  function handleInputUpdate() {
    searchInput.value = query.value
    emit('updateSuggestions', query.value || '')
  }

  const strokeClass = computed(() => {
    if (
      props.variant === 'v2' ||
      props.variant === 'v2-big-yolo' ||
      props.variant === 'v3'
    ) {
      return 'stroke-action-primary-default group-hover:stroke-action-primary-hover group-active:stroke-action-primary-active'
    }
    return 'stroke-[#4B5563] dark:filter dark:invert'
  })

  const styleState = computed(() => {
    const style: {
      container?: string // Container searchbox lives in
      wrapper?: string // Wrapper for the searchbox
      input?: string // Input field
      iconSize?: number // Size of the search and close icons
    } = {}

    // Add bottom border to the container when searchbox is sticky
    if (props.sticky && isSticky.value) {
      style.container = 'border-border-interaction-neutral-normal border-b'
    }

    // DEFAULT variant
    if (props.variant === 'default') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-border-interaction-neutral-normal shadow-searchbox-hover hover:border-border-interaction-neutral-hover border'
          : ''
    }
    // V2 variant
    else if (props.variant === 'v2') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-action-primary-default shadow-searchbox-hover hover:border-action-primary-hover active:border-action-primary-active border-2'
          : ''
    }
    // V2-BIG-YOLO variant
    else if (props.variant === 'v2-big-yolo') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-action-primary-default shadow-searchbox-hover hover:border-action-primary-hover active:border-action-primary-active border-2 py-5'
          : 'py-5'

      style.iconSize = 24

      // Add fully rounded corners to the v2-big-yolo wrapper when suggestions are not visible
      if (!isSuggestionsVisible.value) {
        style.wrapper = `${style.wrapper} rounded-full`
      }
    }
    // V3 variant
    else if (props.variant === 'v3') {
      style.wrapper =
        props.display === 'solid'
          ? 'border-action-primary-default shadow-searchbox-hover hover:border-action-primary-hover active:border-action-primary-active border-2 !pr-1 !py-1'
          : '!pr-1 !py-1'
    }

    // Remove border from wrapper when suggestions are visible
    if (isSuggestionsVisible.value) {
      style.wrapper = style.wrapper
        ? `${style.wrapper} rounded-b-none !border-transparent hover:!border-transparent`
        : 'rounded-b-none !border-transparent hover:!border-transparent'
    }

    // Input style
    if (props.variant === 'v2-big-yolo') {
      style.input = 'text-xl'
    } else {
      style.input = 'text-base'
    }

    return style
  })
</script>
<template>
  <div
    ref="searchboxRef"
    role="search"
    class="flex w-full justify-center bg-inherit"
    :class="[
      { 'sticky top-[-1px] z-20 py-4 px-2': sticky },
      styleState.container,
    ]"
  >
    <div
      v-click-outside="hideSuggestions"
      :class="{ 'max-w-[768px]': sticky }"
      class="relative w-full"
    >
      <div
        class="group bg-background-default shadow-searchbox-default hover:shadow-searchbox-hover active:shadow-searchbox-hover relative z-[11] flex items-center gap-4 rounded-3xl px-6 py-3"
        :class="styleState.wrapper"
      >
        <div class="flex flex-row justify-between flex-grow gap-4">
          <div
            class="flex flex-grow max-w-full items-center justify-start gap-4"
          >
            <SearchIcon
              :size="styleState.iconSize"
              :svg-path-class="strokeClass"
            />
            <input
              id="searchInput"
              v-model.trim="query"
              data-testid="search-input"
              autocomplete="off"
              :autofocus="autofocus"
              :placeholder="t(placeholder)"
              class="flex flex-shrink placeholder-font-normal text-content-primary placeholder-content-secondary caret-content-primary dark:selection:text-content-primary w-full bg-inherit selection:bg-[#3367D1] selection:text-white focus:outline-none"
              :class="styleState.input"
              @keydown.down="handleKeyDown"
              @keydown.up="handleKeyUp"
              @keyup.enter="onEnter"
              @click="openSuggestionsDropdown"
              @input="handleInputUpdate"
            />
          </div>
          <div class="flex flex-row gap-8 items-center">
            <CloseIcon
              v-show="query"
              :size="styleState.iconSize"
              class="cursor-pointer"
              @click="cleanSearchboxInput"
            />
            <ButtonsButton
              v-if="variant === 'v3'"
              size="small"
              rounded
              @click="onSearchButtonPress"
            >
              Search
            </ButtonsButton>
          </div>
        </div>
      </div>
      <div
        v-if="isSuggestionsVisible"
        data-testid="suggestions"
        :class="{ 'shadow-searchbox-focus': isSuggestionsVisible }"
        class="bg-background-default-strong shadow-searchbox-default dark:border-border-interaction-neutral-normal absolute left-0 right-0 z-10 flex w-full flex-col overflow-x-hidden rounded-b-3xl pb-3 pt-1 dark:border-[1px]"
        @mouseleave="handleMouseLeave()"
      >
        <div
          v-if="suggestionHeader && suggestions && suggestions.length > 0"
          class="pb-2 pl-4 pt-3"
        >
          <Typography
            variant="text-xs"
            weight="font-semibold"
            color="text-content-secondary"
          >
            {{ suggestionHeader }}
          </Typography>
        </div>
        <ul>
          <SearchSuggestionItem
            v-for="(suggest, index) in suggestions"
            :key="
              'id' in suggest && typeof suggest.id === 'string'
                ? suggest.id
                : index
            "
            :searchbox-variant="variant"
            :text="suggest.text"
            :description="
              'description' in suggest &&
              typeof suggest.description === 'string'
                ? suggest.description
                : ''
            "
            :image="
              'image' in suggest && typeof suggest.image === 'string'
                ? suggest.image
                : ''
            "
            :query="query"
            :active="index === selectedIndex"
            @mouseover="handleMouseOver(index)"
            @click="handleClickSuggestion(suggest)"
          />
        </ul>
      </div>
    </div>
  </div>
</template>
<style scoped>
  :deep(ul) {
    padding-left: 0;
    margin-bottom: 0;
  }
</style>
